import { el, replaceChildrenWith } from "./dom";
import { looksLikeEmail } from "./format";

/**
 * AB#9981. The export dialog.
 *
 * The export runs outside the request and emails a time-limited link, so this dialog
 * can only ever confirm that the work was QUEUED. It says so in those words: telling
 * a clerk "exported" when a Function has not run yet is how someone ends up waiting
 * for a file that failed silently.
 */

export interface ExportDialogOptions {
  host: HTMLElement;
  trigger: HTMLElement;
  /** Prefill. The address the clerk is signed in as. */
  defaultEmail: string;
  /** Posts the request. Rejects with a message when the server refuses. */
  submit: (recipientEmail: string) => Promise<void>;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function openExportDialog(options: ExportDialogOptions): void {
  const titleId = "spr-export-title";
  const errorId = "spr-export-email-error";

  const dialog = el("div", {
    class: "spr-export-dialog",
    "data-testid": "export-dialog",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": titleId
  });

  const scrim = el("div", { class: "spr-export-scrim" });

  const close = (): void => {
    document.removeEventListener("keydown", onKeyDown, true);
    if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    if (scrim.parentNode) scrim.parentNode.removeChild(scrim);
    options.trigger.focus();
  };

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.prototype.slice.call(
      dialog.querySelectorAll(FOCUSABLE)
    ) as HTMLElement[];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const input = el("input", {
    type: "email",
    id: "spr-export-email",
    class: "spr-input",
    "data-testid": "export-email",
    value: options.defaultEmail,
    autocomplete: "email"
  });

  const errorSlot = el("div", { class: "spr-export-slot" });
  const resultSlot = el("div", { class: "spr-export-slot", "aria-live": "polite" });

  const send = el("button", {
    type: "button",
    class: "spr-btn spr-btn-primary",
    "data-testid": "export-send",
    text: "Send the export"
  });

  const closeButton = el("button", {
    type: "button",
    class: "spr-btn",
    "data-testid": "export-dialog-close",
    text: "Close"
  });
  closeButton.addEventListener("click", close);

  const showFieldError = (message: string): void => {
    input.setAttribute("aria-invalid", "true");
    input.setAttribute("aria-describedby", errorId);
    replaceChildrenWith(errorSlot, [
      el("p", {
        id: errorId,
        class: "spr-error-text",
        "data-testid": "export-email-error",
        text: message
      })
    ]);
  };

  const clearFieldError = (): void => {
    input.removeAttribute("aria-invalid");
    input.removeAttribute("aria-describedby");
    replaceChildrenWith(errorSlot, []);
  };

  send.addEventListener("click", () => {
    const recipient = input.value.trim();

    // Validated before the request, so a typo never reaches the server and the
    // reader is told now rather than by an email that never arrives.
    if (!looksLikeEmail(recipient)) {
      showFieldError(
        recipient.length === 0
          ? "Enter the email address to send the export to."
          : "That does not look like an email address. Check it and try again."
      );
      replaceChildrenWith(resultSlot, []);
      input.focus();
      return;
    }

    clearFieldError();
    send.setAttribute("disabled", "");
    replaceChildrenWith(resultSlot, [
      el("p", { class: "spr-hint", text: "Queueing the export…" })
    ]);

    options.submit(recipient).then(
      () => {
        send.removeAttribute("disabled");
        replaceChildrenWith(resultSlot, [
          el("p", {
            class: "spr-ok-text",
            "data-testid": "export-confirmation",
            text:
              `The export is queued. We will email a download link to ${recipient} when it is ` +
              "ready, usually a minute or two and longer for a large meeting. The link expires " +
              "after seven days."
          })
        ]);
      },
      (error: unknown) => {
        send.removeAttribute("disabled");
        replaceChildrenWith(resultSlot, [
          el("p", {
            class: "spr-error-text",
            "data-testid": "export-error",
            text:
              "We could not queue the export. " +
              (error instanceof Error && error.message ? error.message : "Try again in a moment.")
          })
        ]);
      }
    );
  });

  replaceChildrenWith(dialog, [
    el("h2", { id: titleId, class: "spr-export-title", text: "Where would you like the export sent?" }),
    el("p", {
      class: "spr-hint",
      text:
        "One row per sign-up, as a CSV. It is built in the background and emailed as a " +
        "link, so a large meeting does not time out in the browser."
    }),
    el("div", { class: "spr-field-row" }, [
      el("label", { for: "spr-export-email", class: "spr-label", text: "Email address" }),
      input
    ]),
    errorSlot,
    resultSlot,
    el("div", { class: "spr-export-actions" }, [closeButton, send])
  ]);

  options.host.appendChild(scrim);
  options.host.appendChild(dialog);
  document.addEventListener("keydown", onKeyDown, true);
  input.focus();
  input.select();
}
